import type { ReactNode } from 'react'

type ToastMessageBaseProps = {
  readonly children: ReactNode
  readonly secondaryContent?: ReactNode
}

// closeLabel is required whenever onClose is passed, since the close control needs an accessible name.
export type ToastMessageProps = ToastMessageBaseProps &
  (
    | { readonly onClose?: undefined; readonly closeLabel?: undefined }
    | { readonly onClose: () => void; readonly closeLabel: string }
  )

/*
 * Single-style toast for the page-level, fixed-position notification
 * overlay (see `ToastMessageOverlay`, its only consumer). Purely
 * presentational — the container owns state (which toasts are shown, how
 * they're dismissed) and passes `closeLabel` in already translated, since
 * that label is tied to the container's own notification data, not a fixed
 * generic string.
 *
 * secondaryContent — optional content rendered after `children`, e.g. a
 *                     server-request-id snippet
 */
export function ToastMessage({ children, secondaryContent, onClose, closeLabel }: ToastMessageProps) {
  return (
    <div className="toast-message">
      {onClose && (
        <button
          type="button"
          title={closeLabel}
          aria-label={closeLabel}
          className="toast-message-close"
          onClick={onClose}
        />
      )}
      {children}
      {secondaryContent}
    </div>
  )
}
